import { useEffect, useRef, useState } from "react";

const SEARCH_URL = "https://www.mondialrelay.fr/trouver-le-point-relais-le-plus-proche-de-chez-moi/";
const BAN_SEARCH_URL = "https://data.geopf.fr/geocodage/search";

export default function MondialRelayPicker({ postalCode, onSelect }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [cp, setCp] = useState(postalCode || "");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [confirmedRelay, setConfirmedRelay] = useState(null);
  const timerRef = useRef(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!cp && postalCode) setCp(postalCode);
  }, [postalCode, cp]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const openSearch = () => {
    const params = new URLSearchParams();
    if (cp) {
      params.set("ampcodePostal", cp);
      params.set("codePays", "FR");
    }
    const url = params.toString() ? `${SEARCH_URL}?${params.toString()}` : SEARCH_URL;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const searchAddress = (value) => {
    setAddress(value);
    setConfirmedRelay(null);
    clearTimeout(timerRef.current);

    if (value.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const requestId = ++requestRef.current;
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({
          q: value.trim(),
          limit: "6",
          autocomplete: "1",
        });
        const response = await fetch(`${BAN_SEARCH_URL}?${params.toString()}`);
        if (!response.ok) throw new Error("BAN request failed");
        const data = await response.json();
        if (requestId !== requestRef.current) return;

        const features = Array.isArray(data?.features) ? data.features : [];
        setSuggestions(features.filter((feature) => {
          const type = feature?.properties?.type;
          const kind = feature?.properties?._type;
          return type === "housenumber" || kind === "address" || !type;
        }));
      } catch (error) {
        if (requestId === requestRef.current) setSuggestions([]);
      } finally {
        if (requestId === requestRef.current) setSearching(false);
      }
    }, 250);
  };

  const selectAddress = (feature) => {
    const props = feature?.properties || {};
    const label = props.label || props.name || "";
    const postcode = props.postcode || "";
    const commune = props.city || "";

    setAddress(label);
    if (postcode) setCp(postcode);
    if (commune) setCity(commune);
    setSuggestions([]);
    setConfirmedRelay(null);
  };

  const confirmRelay = () => {
    const relayName = name.trim();
    const relayAddress = address.trim();
    const relayPostalCode = cp.trim();
    const relayCity = city.trim();
    if (!relayName || !relayAddress || !relayPostalCode || !relayCity) return;

    const relay = {
      id: `manual-mondial-relay-${relayPostalCode}-${relayName}-${relayAddress}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, ""),
      name: relayName,
      address: relayAddress,
      postal_code: relayPostalCode,
      city: relayCity,
      country: "FR",
    };

    setConfirmedRelay(relay);
    onSelect?.(relay);
  };

  const selected = Boolean(name.trim() && address.trim() && cp.trim() && city.trim());

  return (
    <div className="mondial-relay-picker">
      <div className="relay-picker-head">
        <strong>Choisissez votre Point Relais / Locker</strong>
        <small>Recherche d'adresse officielle</small>
      </div>
      <p className="relay-picker-message">
        Recherchez votre relais sur Mondial Relay, puis saisissez son nom et son adresse. L'adresse est ensuite proposée automatiquement depuis la Base Adresse Nationale.
      </p>
      <button type="button" className="btn-primary w-full h-12" onClick={openSearch}>
        📍 Rechercher un Point Relais / Locker
      </button>

      <div className="relay-picker-form">
        <input
          value={name}
          onChange={(e) => { setName(e.target.value); setConfirmedRelay(null); }}
          placeholder="Nom du Point Relais / Locker"
          aria-label="Nom du Point Relais"
        />

        <div className="relative">
          <input
            value={address}
            onChange={(e) => searchAddress(e.target.value)}
            placeholder="Adresse du Point Relais"
            aria-label="Adresse du Point Relais"
            autoComplete="off"
          />
          {searching && <div className="text-sm opacity-70 mt-1">Recherche de l'adresse…</div>}
          {suggestions.length > 0 && (
            <div className="relay-address-suggestions" role="listbox">
              {suggestions.map((feature, index) => {
                const props = feature?.properties || {};
                const label = props.label || props.name || "";
                return (
                  <button
                    key={`${props.id || label}-${index}`}
                    type="button"
                    className="w-full text-left p-3"
                    onClick={() => selectAddress(feature)}
                  >
                    📍 {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input value={cp} onChange={(e) => { setCp(e.target.value); setConfirmedRelay(null); }} placeholder="Code postal" aria-label="Code postal du Point Relais" />
          <input value={city} onChange={(e) => { setCity(e.target.value); setConfirmedRelay(null); }} placeholder="Ville" aria-label="Ville du Point Relais" />
        </div>

        <button type="button" className="btn-ghost w-full h-11" onClick={confirmRelay} disabled={!selected}>
          {selected ? "✓ Confirmer ce Point Relais" : "Renseignez le relais choisi"}
        </button>
      </div>

      {confirmedRelay && (
        <div className="relay-picker-selected">
          ✓ {confirmedRelay.name}, {confirmedRelay.address}, {confirmedRelay.postal_code} {confirmedRelay.city}
        </div>
      )}
    </div>
  );
}
